import React, { useState, useRef, useEffect } from "react";
import { Sparkles, Send, X, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getSessionId = () => {
  let id = localStorage.getItem("concierge_session");
  if (!id) {
    id = `web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem("concierge_session", id);
  }
  return id;
};

const SUGGESTIONS = [
  "How much is Brisbane Airport to Surfers?",
  "Plan a hens day for 12",
  "What tours do you run?"
];

export const ConciergeChat = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    if (open && messages.length === 0) {
      fetch(`${BACKEND_URL}/api/concierge/history/${sessionId.current}`)
        .then((r) => r.json())
        .then((h) => {
          if (Array.isArray(h) && h.length > 0) setMessages(h.map((m) => ({ role: m.role, content: m.content })));
        })
        .catch(() => {});
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/concierge/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: msg })
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = JSON.parse(line.slice(6));
          if (data.delta || data.error) {
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = {
                role: "assistant",
                content: next[next.length - 1].content + (data.delta || data.error)
              };
              return next;
            });
          }
        }
      }
    } catch (e) {
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = { role: "assistant", content: "Sorry, I hit a snag — try again or WhatsApp us on +61 481 437 772." };
        return next;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      {/* Launcher */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-20 lg:bottom-6 right-6 z-50 flex items-center gap-2 pl-4 pr-5 py-3 rounded-full bg-gray-950 text-white shadow-2xl border border-amber-500/40 hover:border-amber-400 hover:scale-105 transition-all"
          data-testid="concierge-launcher"
        >
          <Sparkles className="h-5 w-5 text-amber-400" />
          <span className="text-sm font-semibold hidden sm:block">Planning something epic? Ask away</span>
          <span className="text-sm font-semibold sm:hidden">Ask Lexi</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div className="fixed bottom-0 right-0 sm:bottom-6 sm:right-6 z-50 w-full sm:w-96 h-[70vh] sm:h-[560px] bg-white sm:rounded-2xl shadow-2xl border border-black/10 flex flex-col overflow-hidden" data-testid="concierge-panel">
          <div className="bg-gray-950 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-amber-500/20 flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-amber-400" />
              </div>
              <div>
                <p className="text-white font-bold text-sm">Lexi — Limo Near Me</p>
                <p className="text-amber-400/80 text-xs">Ask about prices, tours & bookings</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-white" data-testid="concierge-close">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
            {messages.length === 0 && (
              <div className="space-y-3">
                <div className="bg-white rounded-2xl rounded-tl-sm p-3 text-sm text-foreground border border-black/5 max-w-[85%]">
                  Hey! I'm Lexi ✨ Ask me anything — airport transfer prices, winery tours, party buses, or planning a big day out.
                </div>
                <div className="flex flex-col gap-2">
                  {SUGGESTIONS.map((s, i) => (
                    <button key={i} onClick={() => send(s)} className="text-left text-sm px-4 py-2 rounded-full bg-white border border-amber-300 text-amber-800 hover:bg-amber-50 transition-colors" data-testid={`concierge-suggestion-${i}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`rounded-2xl p-3 text-sm max-w-[85%] whitespace-pre-wrap ${m.role === "user" ? "bg-amber-600 text-white rounded-tr-sm" : "bg-white text-foreground border border-black/5 rounded-tl-sm"}`}>
                  {m.content || (streaming && i === messages.length - 1 ? <Loader2 className="h-4 w-4 animate-spin text-amber-600" /> : "")}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          <div className="p-3 border-t border-black/10 bg-white">
            <div className="flex gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Type your question..."
                className="flex-1 h-11 px-4 rounded-full border border-black/15 text-sm focus:outline-none focus:border-amber-500"
                data-testid="concierge-input"
              />
              <button onClick={() => send()} disabled={streaming || !input.trim()} className="w-11 h-11 rounded-full bg-amber-600 hover:bg-amber-700 disabled:opacity-40 flex items-center justify-center text-white transition-colors" data-testid="concierge-send">
                {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
            <div className="flex justify-center gap-4 mt-2 text-xs">
              <a href="/book" className="text-amber-700 font-semibold hover:underline">Book Now</a>
              <a href="/tours" className="text-amber-700 font-semibold hover:underline">Tours</a>
              <a href="https://wa.me/61481437772" target="_blank" rel="noreferrer" className="text-green-600 font-semibold hover:underline">WhatsApp</a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ConciergeChat;
